/**
 * EndocrineAxesCard — iter1046 (Endocrine1)
 * Двойките хипофизен хормон + хормон на жлезата → на кой етаж е повредата.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Loader2, AlertTriangle, Activity } from "lucide-react";

const STATUS_META = {
  primary: { label: "В жлезата", cls: "bg-rose-500 text-white" },
  central: { label: "Етаж по-горе", cls: "bg-amber-500 text-white" },
  subclinical: { label: "Компенсира", cls: "bg-yellow-500 text-white" },
  resistance: { label: "Несъответствие", cls: "bg-purple-500 text-white" },
  expected_physiology: { label: "Очаквано", cls: "bg-sky-500 text-white" },
  normal: { label: "Работи", cls: "bg-emerald-500 text-white" },
  no_data: { label: "Няма данни", cls: "bg-slate-400 text-white" },
};

export const EndocrineAxesCard = ({ isDark }) => {
  const { API } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [openScreen, setOpenScreen] = useState(false);
  const [openWater, setOpenWater] = useState(false);
  const [openMeasure, setOpenMeasure] = useState(false);
  const [savingWater, setSavingWater] = useState(false);
  const [water, setWater] = useState({});
  const [open, setOpen] = useState(null);
  const [sel, setSel] = useState([]);

  const load = () => axios.get(`${API}/endocrine/axes`, { timeout: 25000 })
    .then(({ data: d }) => {
      setData(d);
      setSel(d.screen_selected || []);
      const lg = d.water_module?.log || {};
      setWater({
        urine_volume_ml: lg.urine_volume_ml ?? "",
        water_intake_ml: lg.water_intake_ml ?? "",
        nocturia_count: lg.nocturia_count ?? "",
        thirst_0_10: lg.thirst_0_10 ?? "",
        urine_color_1_8: lg.urine_color_1_8 ?? "",
      });
    })
    .catch(() => {})
    .finally(() => setLoading(false));

  const saveWater = async () => {
    setSavingWater(true);
    try {
      const payload = {};
      Object.entries(water).forEach(([k, v]) => {
        if (v !== "" && v !== null && v !== undefined) payload[k] = Number(v);
      });
      const { data: d } = await axios.post(`${API}/endocrine/water-log`, payload);
      setData(d);
    } finally { setSavingWater(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [API]);

  const toggle = (k) => setSel((s) => s.includes(k) ? s.filter((x) => x !== k) : [...s, k]);

  const save = async () => {
    setSaving(true);
    try {
      const { data: d } = await axios.post(`${API}/endocrine/screen`, { items: sel });
      setData(d);
      setOpenScreen(false);
    } finally { setSaving(false); }
  };

  const card = isDark ? "bg-slate-900/60 border-slate-800" : "bg-white border-slate-200";

  if (loading) {
    return (
      <div className={`p-4 rounded-2xl border ${card}`} data-testid="endocrine-axes-loading">
        <Loader2 className="w-4 h-4 animate-spin text-violet-500" />
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className={`p-4 rounded-2xl border mb-4 ${card}`} data-testid="endocrine-axes-card">
      <div className="flex items-center gap-2 mb-1">
        <Activity className="w-4 h-4 text-violet-500" />
        <h2 className="font-bold text-sm">Ендокринни оси — къде е повредата</h2>
      </div>
      <p className="text-[11px] opacity-70 mb-3" data-testid="endocrine-summary">{data.summary_bg}</p>

      {/* Предупредителни признаци */}
      {(data.red_flags || []).map((f) => (
        <div
          key={f.key}
          data-testid={`endocrine-flag-${f.key}`}
          className={`p-3 rounded-xl mb-2 border-2 ${
            f.urgency === "emergency"
              ? (isDark ? "bg-rose-900/40 border-rose-500" : "bg-rose-50 border-rose-400")
              : (isDark ? "bg-amber-900/30 border-amber-500/60" : "bg-amber-50 border-amber-300")
          }`}
        >
          <div className="flex items-start gap-2">
            <AlertTriangle className={`w-4 h-4 mt-0.5 ${f.urgency === "emergency" ? "text-rose-500" : "text-amber-500"}`} />
            <div className="min-w-0">
              <div className="text-xs font-bold">{f.title_bg}</div>
              <p className="text-[11px] mt-1 leading-relaxed opacity-90">{f.text_bg}</p>
              <div className="text-[11px] mt-1.5 font-semibold">{f.action_bg}</div>
            </div>
          </div>
        </div>
      ))}

      {/* Осите */}
      <div className="space-y-2">
        {(data.axes || []).map((a) => {
          const meta = STATUS_META[a.status] || STATUS_META.no_data;
          const isOpen = open === a.key;
          return (
            <div
              key={a.key}
              className={`rounded-xl border ${isDark ? "border-slate-800 bg-slate-800/30" : "border-slate-200 bg-slate-50"}`}
              data-testid={`endocrine-axis-${a.key}`}
            >
              <button
                onClick={() => setOpen(isOpen ? null : a.key)}
                data-testid={`endocrine-axis-toggle-${a.key}`}
                className="w-full text-left p-3 flex items-center gap-2"
              >
                <span className="text-[11px] font-bold flex-1 min-w-0">{a.title_bg}</span>
                <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-bold shrink-0 ${meta.cls}`}
                      data-testid={`endocrine-axis-status-${a.key}`}>
                  {meta.label}
                </span>
                <span className="text-[10px] opacity-50 shrink-0">{isOpen ? "−" : "+"}</span>
              </button>
              {isOpen && (
                <div className="px-3 pb-3 space-y-2">
                  <div className="text-[11px] font-semibold">{a.label_bg}</div>
                  <div className="text-[10px] opacity-60">{a.chain_bg}</div>
                  <p className="text-[11px] leading-relaxed opacity-90">{a.explain_bg}</p>
                  {(a.values || []).length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {a.values.map((v) => (
                        <span key={v.label_bg}
                              className={`text-[10px] px-2 py-0.5 rounded-md ${isDark ? "bg-slate-900/70" : "bg-white border border-slate-200"}`}>
                          {v.label_bg}: <strong>{v.value}</strong> {v.unit}
                        </span>
                      ))}
                    </div>
                  )}
                  {(a.missing || []).length > 0 && (
                    <div className={`p-2 rounded-lg text-[10px] ${isDark ? "bg-slate-900/60" : "bg-white border border-slate-200"}`}
                         data-testid={`endocrine-missing-${a.key}`}>
                      <div className="font-bold opacity-70 mb-1">Какво липсва, за да се реши</div>
                      {a.missing.map((m) => (
                        <div key={m.marker} className="mb-0.5">
                          <strong>{m.label_bg}</strong> — {m.why_bg}
                        </div>
                      ))}
                    </div>
                  )}
                  {a.measure_note_bg && (
                    <div className="text-[10px] italic opacity-70" data-testid={`endocrine-measure-${a.key}`}>
                      Как се мери правилно: {a.measure_note_bg}
                    </div>
                  )}
                  {(a.links || []).map((l, i) => (
                    <button
                      key={`${a.key}-${i}`}
                      onClick={() => navigate(l.route)}
                      data-testid={`endocrine-link-${a.key}-${i}`}
                      className={`w-full text-left p-2 rounded-lg text-[10px] transition-colors ${
                        l.urgency === "high"
                          ? (isDark ? "bg-rose-500/15 hover:bg-rose-500/25" : "bg-rose-50 hover:bg-rose-100")
                          : (isDark ? "bg-violet-500/10 hover:bg-violet-500/20" : "bg-violet-50 hover:bg-violet-100")
                      }`}
                    >
                      <span className="font-bold">{l.title_bg} →</span>
                      <span className="opacity-80"> {l.action_bg}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* iter1052 — „Как се мери правилно": биотин, TBG/естроген, тайминг по цикъл */}
      {data.measure_module && (
        <div className={`rounded-xl border mb-3 ${isDark ? "border-slate-800 bg-slate-800/30" : "border-slate-200 bg-slate-50"}`}
             data-testid="measure-module">
          <button onClick={() => setOpenMeasure((v) => !v)}
                  data-testid="measure-module-toggle"
                  className="w-full text-left p-3 flex items-center gap-2">
            <span className="text-[11px] font-bold flex-1 min-w-0">{data.measure_module.title_bg}</span>
            <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-bold shrink-0 whitespace-nowrap ${
              data.measure_module.status === "action" ? "bg-rose-500 text-white"
              : data.measure_module.status === "watch" ? "bg-amber-500 text-white"
              : "bg-emerald-500 text-white"
            }`} data-testid="measure-module-status">{data.measure_module.label_bg}</span>
            <span className="text-[10px] opacity-50">{openMeasure ? "−" : "+"}</span>
          </button>
          {openMeasure && (
            <div className="px-3 pb-3 space-y-2">
              <p className="text-[11px] leading-relaxed opacity-90">{data.measure_module.explain_bg}</p>
              <button onClick={() => navigate("/dashboard/lab-sheet")}
                      data-testid="measure-lab-sheet-btn"
                      className="w-full py-2 rounded-lg bg-violet-600 text-white text-[11px] font-bold hover:bg-violet-700 transition-colors">
                Моят лист за изследване (за печат) →
              </button>
              {(data.measure_module.personal || []).length > 0 && (
                <div className={`p-2 rounded-lg text-[10px] ${isDark ? "bg-rose-500/10" : "bg-rose-50 border border-rose-200"}`}
                     data-testid="measure-personal">
                  <div className="font-bold opacity-80 mb-1">Отнася се точно за Вас</div>
                  {data.measure_module.personal.map((p) => (
                    <div key={p.title_bg} className="mb-1">
                      <strong>{p.title_bg}</strong> — {p.personal_bg}
                    </div>
                  ))}
                </div>
              )}
              {(data.measure_module.rules || []).map((r) => (
                <div key={r.key} data-testid={`measure-rule-${r.key}`}
                     className={`p-2 rounded-lg text-[10px] ${
                       r.applies
                         ? (isDark ? "bg-violet-500/10" : "bg-violet-50 border border-violet-200")
                         : (isDark ? "bg-slate-900/50" : "bg-white border border-slate-200")
                     }`}>
                  <div className="font-bold flex items-center gap-1">
                    {r.title_bg}
                    {r.applies && (
                      <span className="text-[8px] px-1 py-0.5 rounded-full bg-violet-500 text-white">важи за Вас</span>
                    )}
                  </div>
                  <div className="mt-0.5"><strong>Кога:</strong> {r.when_bg}</div>
                  <div className="opacity-85 mt-0.5">{r.why_bg}</div>
                  {r.personal_bg && <div className="mt-0.5 font-semibold">{r.personal_bg}</div>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* iter1048 — воден баланс (ADH): дневник + разграничение DI срещу SIADH */}
      {data.water_module && (
        <div className={`rounded-xl border mb-3 ${isDark ? "border-slate-800 bg-slate-800/30" : "border-slate-200 bg-slate-50"}`}
             data-testid="water-module">
          <button
            onClick={() => setOpenWater((v) => !v)}
            data-testid="water-module-toggle"
            className="w-full text-left p-3 flex items-center gap-2"
          >
            <span className="text-[11px] font-bold flex-1 min-w-0">
              Воден баланс — ADH и осмолалитет
            </span>
            <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-bold shrink-0 ${
              data.water_module.pattern === "no_data" ? "bg-slate-400 text-white"
              : data.water_module.pattern === "normal" ? "bg-emerald-500 text-white"
              : "bg-amber-500 text-white"
            }`} data-testid="water-module-pattern">
              {data.water_module.label_bg}
            </span>
            <span className="text-[10px] opacity-50">{openWater ? "−" : "+"}</span>
          </button>
          {openWater && (
            <div className="px-3 pb-3 space-y-2">
              <p className="text-[11px] leading-relaxed opacity-90">{data.water_module.explain_bg}</p>
              <p className="text-[10px] italic opacity-75" data-testid="water-mechanism">
                {data.water_module.mechanism_bg}
              </p>
              {(data.water_module.signals || []).length > 0 && (
                <ul className="space-y-0.5" data-testid="water-signals">
                  {data.water_module.signals.map((s) => (
                    <li key={s} className="text-[10px] opacity-80">• {s}</li>
                  ))}
                </ul>
              )}
              {(data.water_module.levers || []).map((l) => (
                <div key={l} className="text-[10px] font-semibold opacity-85">→ {l}</div>
              ))}
              {(data.water_module.missing || []).length > 0 && (
                <div className={`p-2 rounded-lg text-[10px] ${isDark ? "bg-slate-900/60" : "bg-white border border-slate-200"}`}
                     data-testid="water-missing">
                  <div className="font-bold opacity-70 mb-1">Какво липсва, за да се реши</div>
                  {data.water_module.missing.map((m) => (
                    <div key={m.marker} className="mb-0.5">
                      <strong>{m.label_bg}</strong> — {m.why_bg}
                    </div>
                  ))}
                </div>
              )}
              {(data.water_module.links || []).map((l, i) => (
                <button
                  key={`water-${i}`}
                  onClick={() => navigate(l.route)}
                  data-testid={`water-link-${i}`}
                  className={`w-full text-left p-2 rounded-lg text-[10px] transition-colors ${
                    l.urgency === "high"
                      ? (isDark ? "bg-rose-500/15 hover:bg-rose-500/25" : "bg-rose-50 hover:bg-rose-100")
                      : (isDark ? "bg-violet-500/10 hover:bg-violet-500/20" : "bg-violet-50 hover:bg-violet-100")
                  }`}
                >
                  <span className="font-bold">{l.title_bg} →</span>
                  <span className="opacity-80"> {l.action_bg}</span>
                </button>
              ))}

              {/* Дневник за един ден */}
              <div className="grid grid-cols-2 gap-2 pt-1">
                {[
                  ["urine_volume_ml", "Обем урина (ml/24ч)"],
                  ["water_intake_ml", "Изпита вода (ml)"],
                  ["nocturia_count", "Ставания нощем"],
                  ["thirst_0_10", "Жажда 0-10"],
                  ["urine_color_1_8", "Цвят урина 1-8"],
                ].map(([k, lbl]) => (
                  <label key={k} className="text-[10px] opacity-80">
                    {lbl}
                    <input
                      type="number"
                      value={water[k] ?? ""}
                      onChange={(e) => setWater((w) => ({ ...w, [k]: e.target.value }))}
                      data-testid={`water-input-${k}`}
                      className={`w-full mt-0.5 px-2 py-1 rounded-lg text-[11px] ${
                        isDark ? "bg-slate-900 border border-slate-700" : "bg-white border border-slate-300"
                      }`}
                    />
                  </label>
                ))}
              </div>
              <button
                onClick={saveWater}
                disabled={savingWater}
                data-testid="water-log-save"
                className="w-full py-2 rounded-lg bg-violet-600 text-white text-[11px] font-bold disabled:opacity-60"
              >
                {savingWater ? "Записвам…" : "Запиши водния дневник"}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Кратка проверка по признаци */}
      <button
        onClick={() => setOpenScreen((v) => !v)}
        data-testid="endocrine-screen-toggle"
        className={`mt-3 w-full text-[11px] font-semibold py-2 rounded-lg transition-colors ${
          isDark ? "bg-violet-500/15 text-violet-200 hover:bg-violet-500/25"
                 : "bg-violet-50 text-violet-700 hover:bg-violet-100"
        }`}
      >
        {openScreen ? "Скрий въпросите" : `Кратка проверка по признаци (${(data.screen_selected || []).length} отбелязани)`}
      </button>
      {openScreen && (
        <div className="mt-2 space-y-1.5" data-testid="endocrine-screen">
          {(data.screen_items || []).map((it) => (
            <label key={it.key}
                   data-testid={`endocrine-screen-${it.key}`}
                   className={`flex items-start gap-2 p-2 rounded-lg cursor-pointer text-[11px] ${
                     sel.includes(it.key)
                       ? (isDark ? "bg-violet-500/20" : "bg-violet-50")
                       : (isDark ? "bg-slate-800/40" : "bg-slate-50")
                   }`}>
              <input type="checkbox" checked={sel.includes(it.key)}
                     onChange={() => toggle(it.key)} className="mt-0.5" />
              <span>
                <span className="opacity-50 text-[9px] uppercase font-bold block">{it.group_bg}</span>
                {it.label_bg}
              </span>
            </label>
          ))}
          <button
            onClick={save}
            disabled={saving}
            data-testid="endocrine-screen-save"
            className="w-full mt-1 py-2 rounded-lg bg-violet-600 text-white text-[11px] font-bold disabled:opacity-60"
          >
            {saving ? "Записвам…" : "Запиши"}
          </button>
        </div>
      )}

      {(data.medications || []).length > 0 && (
        <div className={`mt-3 p-2.5 rounded-lg text-[10px] ${isDark ? "bg-slate-800/50" : "bg-slate-50 border border-slate-200"}`}
             data-testid="endocrine-meds">
          <div className="font-bold opacity-70 mb-1">Лекарства от Вашия списък, които влияят на осите</div>
          {data.medications.map((m) => (
            <div key={m.name} className="mb-1">
              <strong>{m.name}</strong> ({m.group_bg}) — {m.why_bg}
            </div>
          ))}
        </div>
      )}

      <p className="text-[10px] italic opacity-60 mt-3" data-testid="endocrine-feedback-note">
        {data.feedback_bg}
      </p>
    </div>
  );
};

export default EndocrineAxesCard;
